import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Store, BookOpen, RefreshCw, HeartHandshake, ArrowRight, Wallet } from 'lucide-react';
import { PhoneImage } from '../components/site/PhoneImage';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-508" data-mp="1zjs61syu8l3z">
    {children}
  </motion.div>;
export function Home() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-509" data-mp="3kv0ox6qnoitj">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-510" data-mp="30vqf8fay7gze"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto relative z-10" data-id="element-511" data-mp="2mxs4djvu62on">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-512" data-mp="2hyffktlemljf">
          <FadeIn data-id="element-513" data-mp="2i007ebgc3qdl">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center gap-2" data-id="element-514" data-mp="2d7731oos2k21">
              <MapPin size={16} className="text-brand-yellow" data-id="element-515" data-mp="288ngpn69v695" />
              {t.home.heroEyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 leading-[1.1] tracking-tight" data-id="element-516" data-mp="236y5o7j1usl2">
              {t.home.heroTitle1}{' '}
              <span className="relative inline-block" data-id="element-517" data-mp="1u8usbyi7x24c">
                <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-518" data-mp="1kk7f0qgov4dn">
                  {t.home.heroTitle2}
                </span>
                {/* SVG Highlight */}
                <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-519" data-mp="1fldzkshud3au">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-520" data-mp="28akyhsl7juwm" />
                </svg>
              </span>
            </h1>
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-10 leading-relaxed max-w-lg" data-id="element-521" data-mp="2m8hzr2alwphm">
              {t.home.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-522" data-mp="fl0qfouuqqtc">
              <span className="bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-8 py-4 rounded-full text-sm font-bold tracking-wide uppercase text-center cursor-default select-none border border-brand-green/20 dark:border-brand-green/30" data-id="element-523" data-mp="fjhe29cbgzwu">
                {t.common.disponivelEmBreve}
              </span>
              <Link to="/para-voce" className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-brand-charcoal dark:text-white border border-gray-200 dark:border-gray-700 px-8 py-4 rounded-full text-base font-bold transition-all text-center shadow-sm hover:shadow-md flex items-center justify-center gap-2 group" data-id="element-524" data-mp="3stdotcuwljl0">
                {t.common.saibaMais}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-525" data-mp="1h0yk2hva7gvn" />
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} className="relative flex justify-center lg:justify-end" data-id="element-526" data-mp="2ccpyagyyr3y1">
            <div className="absolute inset-0 bg-brand-green/5 dark:bg-brand-green/10 rounded-full blur-3xl -z-10 transform scale-150" data-id="element-527" data-mp="2hbt2jz74d64y"></div>
            <PhoneImage src="https://cdn.magicpatterns.com/uploads/cgbjn1qcDNvkL481RjnmnH/AppHome.png" alt="Tela inicial do app BairrouApp mostrando o feed do bairro" className="transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-528" data-mp="2m92rldqljfs9" />
          </FadeIn>
        </div>
      </section>

      {/* Trusted By Strip */}
      <section className="border-y border-gray-200/50 dark:border-gray-800 bg-white/50 dark:bg-gray-800/50 py-10 px-6" data-id="element-529" data-mp="3513z14hfseda">
        <div className="max-w-7xl mx-auto text-center" data-id="element-530" data-mp="39yndo5i1qt5r">
          <p className="text-sm font-bold tracking-wider uppercase text-brand-charcoal/40 dark:text-gray-400 mb-8" data-id="element-531" data-mp="3e3d8rs4vs3pm">
            {t.common.trustedBy}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 dark:opacity-30 grayscale" data-id="element-532" data-mp="ydegqa8ojuj3">
            {/* Placeholder Logos */}
            <div className="h-8 w-32 bg-brand-charcoal/20 dark:bg-white/20 rounded" data-id="element-533" data-mp="tejzhcqd2e13"></div>
            <div className="h-8 w-24 bg-brand-charcoal/20 dark:bg-white/20 rounded" data-id="element-534" data-mp="ogrv3j8j4zg0"></div>
            <div className="h-8 w-36 bg-brand-charcoal/20 dark:bg-white/20 rounded" data-id="element-535" data-mp="1m1nxgxr1ds3d"></div>
            <div className="h-8 w-28 bg-brand-charcoal/20 dark:bg-white/20 rounded hidden sm:block" data-id="element-536" data-mp="1h4xiyeuvr4k2"></div>
            <div className="h-8 w-32 bg-brand-charcoal/20 dark:bg-white/20 rounded hidden md:block" data-id="element-537" data-mp="1cbuxyv4653fh"></div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-538" data-mp="24tqh8hyz5ohu">
        <div className="max-w-7xl mx-auto" data-id="element-539" data-mp="201p8h610mfwk">
          <FadeIn className="text-center mb-20" data-id="element-540" data-mp="200no2ay40h26">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-541" data-mp="3uacc6qubrz4e">
              {t.home.comoFunciona}
            </h2>
            <p className="text-lg text-brand-charcoal/60 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-542" data-mp="3klawl1rozjqy">
              {t.home.comoFuncionaDesc}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-543" data-mp="3amv3it93u0et">
            {/* Dotted connector line (desktop only) */}
            <div className="hidden md:block absolute top-1/2 left-1/6 right-1/6 h-0.5 border-t-2 border-dashed border-brand-green/20 dark:border-brand-green/10 -translate-y-1/2 z-0" aria-hidden="true" data-id="element-544" data-mp="3fly7sbh9g2lq"></div>

            {[{
            icon: <MapPin size={28} className="text-brand-green dark:text-brand-green-light" data-id="element-545" data-mp="zvljat6n060d" />,
            title: t.home.step1Title,
            desc: t.home.step1Desc
          }, {
            icon: <Wallet size={28} className="text-brand-green dark:text-brand-green-light" data-id="element-546" data-mp="19zs1rdwqsdog" />,
            title: t.home.step2Title,
            desc: t.home.step2Desc
          }, {
            icon: <HeartHandshake size={28} className="text-brand-green dark:text-brand-green-light" data-id="element-547" data-mp="1xmi6wo4e0oda" />,
            title: t.home.step3Title,
            desc: t.home.step3Desc
          }].map((step, i) => <FadeIn key={i} delay={i * 0.1} className="bg-brand-cream/80 dark:bg-gray-900/80 p-10 rounded-3xl border border-brand-yellow/20 dark:border-brand-yellow/10 text-center hover:bg-brand-cream dark:hover:bg-gray-900 transition-colors relative z-10 shadow-sm" data-id="element-548" data-mp="22nxt4jiax0x9" data-mpi={i}>
                <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-8 shadow-md border-2 border-brand-yellow/30 dark:border-brand-yellow/20 text-brand-yellow" data-id="element-549" data-mp="11swhyjnnof6z">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-550" data-mp="1anklo5dl1oto">
                  {step.title}
                </h3>
                <p className="text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-551" data-mp="itsedqevvdmp">
                  {step.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Pilares / Features */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-552" data-mp="rsvuclir7cwz">
        <FadeIn className="text-center mb-20" data-id="element-553" data-mp="1ya99t8t6qdiq">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-554" data-mp="23a5bv2ibnuvn">
            {t.home.featuresEyebrow}
          </span>
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-555" data-mp="2dfww9xx2mnrm">
            {t.home.pilaresTitle}
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-556" data-mp="23m6d98jpr5jb">
          {[{
          icon: <Store size={24} data-id="element-557" data-mp="1zkurxf7vh6gx" />,
          title: t.home.pilar1Title,
          desc: t.home.pilar1Desc
        }, {
          icon: <BookOpen size={24} data-id="element-558" data-mp="1pngbhfly8hom" />,
          title: t.home.pilar2Title,
          desc: t.home.pilar2Desc
        }, {
          icon: <RefreshCw size={24} data-id="element-559" data-mp="1kr81egmlzeob" />,
          title: t.home.pilar3Title,
          desc: t.home.pilar3Desc
        }, {
          icon: <HeartHandshake size={24} data-id="element-560" data-mp="3f24pfla6u7or" />,
          title: t.home.pilar4Title,
          desc: t.home.pilar4Desc
        }].map((feature, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-id="element-561" data-mp="co20jy1y87h3" data-mpi={i}>
              <div className="w-12 h-12 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6" data-id="element-562" data-mp="mket2slrcfa9">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-563" data-mp="3l568recgb08w">
                {feature.title}
              </h3>
              <p className="text-sm text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-564" data-mp="3p7w5lidmcsz">
                {feature.desc}
              </p>
            </FadeIn>)}
        </div>
      </section>

      {/* Audiences */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-565" data-mp="32dwxazj4e1eo">
        {/* Organic shape background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/20 dark:bg-brand-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-566" data-mp="1htrx43ph53e6"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-567" data-mp="1cwi6ikqcmlor"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-568" data-mp="25k5ort45d088">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-569" data-mp="1rlxwvzlywp3x">
            <FadeIn data-id="element-570" data-mp="1rns64gnvmss6">
              <span className="text-brand-green font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-571" data-mp="1wkr274r3p9t0">
                {t.home.audiencesEyebrow}
              </span>
              <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-white mb-6 tracking-tight" data-id="element-572" data-mp="g57lm22ot73g">
                {t.home.audiencesTitle}
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed" data-id="element-573" data-mp="2mtq3ndbzjg6a">
                {t.home.audiencesDesc}
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="relative flex justify-center lg:justify-end" data-id="element-574" data-mp="2d4yc36au6xvw">
              <div className="absolute inset-0 bg-brand-green/10 dark:bg-brand-green/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-575" data-mp="292bjh36cd8xn"></div>
              <PhoneImage src="https://cdn.magicpatterns.com/uploads/2GZRC4gWNWPUkcDVK85Szk/Offer.png" alt={t.home.mockAlt2} className="transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-576" data-mp="242727axm0a6d" />
            </FadeIn>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-577" data-mp="1q6v732fjkhox">
            {[{
            title: t.home.aud1Title,
            desc: t.home.aud1Desc,
            link: '/para-voce'
          }, {
            title: t.home.aud2Title,
            desc: t.home.aud2Desc,
            link: '/para-negocio'
          }, {
            title: t.home.aud3Title,
            desc: t.home.aud3Desc,
            link: '/para-marca'
          }].map((card, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white/5 dark:bg-white/5 p-10 rounded-3xl border border-white/10 dark:border-white/5 flex flex-col h-full hover:bg-white/10 dark:hover:bg-white/10 transition-colors" data-id="element-578" data-mp="1k142nw3ji824" data-mpi={i}>
                <h3 className="text-2xl font-bold mb-4" data-id="element-579" data-mp="wcbbse1ryqed">{card.title}</h3>
                <p className="text-white/60 mb-10 flex-grow leading-relaxed" data-id="element-580" data-mp="11bfnpcg3328e">
                  {card.desc}
                </p>
                <Link to={card.link} className="flex items-center gap-2 text-brand-green dark:text-brand-green-light font-bold hover:gap-3 transition-all group" data-id="element-581" data-mp="27qq0og86eh8j">
                  {t.common.saibaMais}{' '}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-582" data-mp="26akljfk2sffq" />
                </Link>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center max-w-4xl mx-auto relative z-10" data-id="element-583" data-mp="2u7zyyjj57ri8">
        <FadeIn data-id="element-584" data-mp="2p9q4yc95mwy7">
          <h2 className="text-5xl md:text-6xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 tracking-tight" data-id="element-585" data-mp="2pa8ajzhpinis">
            {t.home.ctaTitle}
          </h2>
          <p className="text-xl text-brand-charcoal/60 dark:text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-586" data-mp="2yz9n1fp1mms0">
            {t.home.ctaDesc}
          </p>
          <span className="inline-block bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-10 py-4 rounded-full text-sm font-bold tracking-wide uppercase cursor-default select-none border border-brand-green/20 dark:border-brand-green/30" data-id="element-587" data-mp="3czkf2m23a7er">
            {t.common.disponivelEmBreve}
          </span>
        </FadeIn>
      </section>
    </div>;
}